import {
  createElement,
  useState,
  useCallback,
  useEffect,
  useRef,
  FC,
  ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { ToastContext } from './context';

interface QueuedToastRenderProps {
  animationDuration: number;
  expired: boolean;
}

export interface QueuedToast {
  render: (props: QueuedToastRenderProps) => ReactNode;
  timeout?: number;
  onComplete?: () => void;
}

/**
 * This is the provider for the ToastContext. In addition to providing context,
 * it also manages the notification queue.
 */
export const Notifications: FC = ({ children }) => {
  const [queue, setQueue] = useState<QueuedToast[]>([]);
  const [fadingOut, setFadingOut] = useState(false);
  const timer = useRef<number>();

  // default duration that the toast should show for
  const defaultTimeout = 3500;
  // duration of the enter/exit animations
  const animationDuration = 450;

  // dispatch a new notification to the queue
  const dispatch = useCallback(
    (toast: QueuedToast) => setQueue((prev) => [...prev, toast]),
    [setQueue]
  );

  // shift the queue to display the next notification
  // we also will reset the toast lifecycle here
  const next = useCallback(() => {
    setFadingOut(() => false);
    timer.current = undefined;
    setQueue((prev) => prev.slice(1));
  }, []);

  useEffect(() => {
    const currentToast = queue[0];
    // if nothings in our queue or a timer is already going, exit early
    if (!currentToast || timer.current) {
      return;
    }

    // start the lifecycle timer
    timer.current = setTimeout(() => {
      // start the fadeout animation and timeout
      setFadingOut(() => true);
      timer.current = setTimeout(() => {
        // animation is complete, handle completion logic and move to next
        if (currentToast.onComplete) {
          currentToast.onComplete();
        }
        next();
      }, animationDuration);
    }, currentToast.timeout || defaultTimeout);
  }, [next, queue]);

  // clear any existing timeouts on unmount
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  return (
    <ToastContext.Provider
      value={{
        queue,
        dispatch,
        next
      }}
    >
      {children}
      {queue[0]
        ? createPortal(
            queue[0].render({ animationDuration, expired: fadingOut }),
            document.body
          )
        : null}
    </ToastContext.Provider>
  );
};
